import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { SeasonProvider, useSeasonContext } from "@/contexts/SeasonContext";
import heroImage from "@/assets/hero-castle.png";
import logoOyster from "@/assets/logo-oyster.png";

// --- COMPOSANT INTERNE : ÉCRAN DE CHARGEMENT ---
const LoadingScreen = ({ onComplete }: { onComplete: () => void }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(onComplete, 500);
          return 100;
        }
        return prev + 1.5;
      });
    }, 20);
    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-[100] bg-cream flex flex-col items-center justify-center">
      <div className="flex flex-col items-center px-4">
        <img src={logoOyster} alt="Logo L'Ostréa - Restaurant privatisable à Mazères" className="h-20 md:h-28 w-auto mb-6 animate-pulse" fetchPriority="high" />
        <h2 className="text-2xl md:text-3xl text-warm-brown mb-8" style={{ fontFamily: "Cinzel, serif" }}>
          L'Ostrea
        </h2>
        <div className="w-48 md:w-64 h-[2px] bg-warm-brown/10 rounded-full overflow-hidden">
          <div className="h-full bg-ochre transition-all duration-300 ease-out" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  );
};

// --- COMPOSANT DE CONTENU ---
const HeroContent = ({ isLoading }: { isLoading: boolean }) => {
  const { isSummer } = useSeasonContext();

  const btnReserveClass = isSummer
    ? "bg-summer-accent hover:bg-summer-accent-dark shadow-soft"
    : "bg-ochre hover:bg-ochre-dark shadow-soft";

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={heroImage} 
          alt="Restaurant L'Ostréa à Mazères - Salle privatisable pour repas de groupe" 
          className="w-full h-full object-cover"
          width="1920"
          height="1080"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-warm-brown/50 via-warm-brown/50 to-warm-brown/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <h1
          className={`font-serif text-4xl md:text-6xl lg:text-7xl text-cream mb-6 leading-tight transition-all duration-1000 ease-out ${
            !isLoading ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
          style={{ transitionDelay: "0.2s" }}
        >
          L'Ostréa, votre guinguette éphémère à Mazères
        </h1>

        <p
          className={`text-lg md:text-xl text-cream/90 mb-10 font-light max-w-2xl mx-auto transition-all duration-1000 ease-out ${
            !isLoading ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
          style={{ transitionDelay: "0.4s" }}
        >
          Votre guinguette éphémère à Mazères pour vos déjeuners et dîners.
        </p>

        <div
          className={`flex flex-col sm:flex-row gap-4 justify-center items-center transition-all duration-1000 ease-out ${
            !isLoading ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
          style={{ transitionDelay: "0.6s" }}
        >
          {/* Bouton Réserver : Hauteur fixe pour l'alignement */}
          <a
            href="#contact"
            className={`inline-flex items-center justify-center px-8 h-[52px] text-white font-medium rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 ${btnReserveClass}`}
          >
            Réservation Obligatoire
          </a>

          {/* Bouton Découvrir : Hauteur fixe et hover translucide ajusté */}
          <a
            href="#about"
            className="inline-flex items-center justify-center px-8 h-[52px] border-2 border-white/30 bg-white/10 backdrop-blur-sm text-cream font-medium rounded-lg hover:bg-white/25 hover:border-white/50 transition-all duration-300 transform hover:-translate-y-0.5"
          >
            Découvrir
          </a>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className={`hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 text-cream/80 transition-all duration-1000 ${
          !isLoading ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
        style={{ transitionDelay: "1s" }}
      >
          <a href="#about" aria-label="Défiler vers la section À propos" className="hover:text-cream transition-colors animate-float block">
            <ChevronDown size={32} />
          </a>
      </div>
    </section>
  );
};

// --- COMPOSANT PRINCIPAL ---
const HeroSection = () => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <SeasonProvider>
      {isLoading && <LoadingScreen onComplete={() => setIsLoading(false)} />}
      <HeroContent isLoading={isLoading} />
    </SeasonProvider>
  );
};

export default HeroSection;
